import React, { useRef, useEffect, useLayoutEffect, useState, useCallback, ReactNode, CSSProperties } from 'react';
import Portal from 'carbon-react/lib/components/portal';

interface Props {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  offset?: { x: number; y: number };
  id?: string;
  style?: CSSProperties;
  customClassName?: string;
  childDialogOpen?: boolean;
}

const EDGE_PADDING = 32;
const DATE_PICKER_ID = 'styled-day-picker';
const MUI_PICKERS_POPPER_CLASS = 'MuiPickersPopper-root';

export const CustomPopover = ({
  anchorEl,
  open,
  onClose,
  children,
  offset = { x: 0, y: 0 },
  id,
  style,
  customClassName,
  childDialogOpen
}: Props) => {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // carbon-react's <Portal> creates its DOM target asynchronously inside its own effect
  // (it renders null on the first commit, then mounts into the real DOM on a later commit
  // once that target exists). A plain useRef doesn't trigger a re-render/effect re-run when
  // it eventually gets attached, so we track attachment via a callback ref + state as well,
  // and include that state in the position effect's dependencies below. Without this, the
  // position effect can run once (while the ref is still null), get skipped, and never run
  // again - leaving the popover stuck at its default { top: 0, left: 0 }.
  const [isPopoverAttached, setIsPopoverAttached] = useState(false);
  const setPopoverRef = useCallback((node: HTMLDivElement | null) => {
    popoverRef.current = node;
    setIsPopoverAttached(!!node);
  }, []);

  // Handle clicks outside the popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !childDialogOpen &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        // Check if the clicked target is within the element with id="styled-day-picker"
        const datePickerEl = document.getElementById(DATE_PICKER_ID);
        if (datePickerEl && datePickerEl.contains(event.target as Node)) {
          return; // Click is within the date picker, so do not close the popover
        }
        // Check if the clicked target is within a MUI date pickers popper (calendar portal)
        const muiPickersPopper = document.querySelector(`.${MUI_PICKERS_POPPER_CLASS}`);
        if (muiPickersPopper && muiPickersPopper.contains(event.target as Node)) {
          return; // Click is within the MUI calendar popper, so do not close the popover
        }
        onClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose, anchorEl, childDialogOpen]);

  // Calculate position
  useLayoutEffect(() => {
    if (open && anchorEl && popoverRef.current && typeof anchorEl.getBoundingClientRect === 'function') {
      const rect = anchorEl.getBoundingClientRect();
      const popover = popoverRef.current;
      const { offsetWidth: popoverWidth, offsetHeight: popoverHeight } = popover;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = rect.bottom + offset.y + window.scrollY;
      let left = rect.left + offset.x + window.scrollX;

      // Adjust horizontally if overflowing
      if (left + popoverWidth > viewportWidth) {
        left = Math.max(viewportWidth - popoverWidth - EDGE_PADDING, EDGE_PADDING);
      }

      // Adjust vertically if overflowing
      if (top + popoverHeight > viewportHeight) {
        const aboveTop = rect.top - popoverHeight - offset.y + window.scrollY;
        if (aboveTop > 0) {
          top = aboveTop;
        } else {
          top = Math.max(viewportHeight - popoverHeight - EDGE_PADDING, EDGE_PADDING);
        }
      }

      setPosition({ top, left });
    }
  }, [open, anchorEl, offset.x, offset.y, isPopoverAttached]);

  if (!open || !anchorEl) {
    return null;
  }

  const popoverStyles: React.CSSProperties = {
    position: 'absolute',
    top: position.top,
    left: position.left,
    zIndex: 1000,
    backgroundColor: '#fff',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    borderRadius: '4px',
    padding: '8px',
    maxHeight: '300px',
    maxWidth: '90vw',
    overflowY: 'auto',
    overflowX: 'hidden',
    ...style
  };

  return (
    <Portal>
      <div
        ref={setPopoverRef}
        role="dialog"
        aria-hidden={!open}
        aria-labelledby={id ? `${id}-label` : undefined}
        aria-describedby={id ? `${id}-description` : undefined}
        aria-label={id}
        style={popoverStyles}
        className={customClassName}
      >
        {children}
      </div>
    </Portal>
  );
};
